import type { FC } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form-latest';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { _isNil, _isNotBlank, _notNil } from '@/littledash';

import type { State } from '@/model/State.model';
import InVivoError from '@/model/InVivoError.ts';

import { capitalise, missingNameUseEmail, successToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { reviewActionToTextMap } from '@/referenceData/study/status';
import * as Auth from '@/support/auth';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';

interface Approver {
  id: number;
  name: string;
  email: string;
  response: string | null;
}

export interface ConfirmStudyApprovalProps {
  approvalID: number;
  status: 'approve' | 'reject';
  callback: (error?: Error) => void;
  approvers: Array<Approver>;
}

interface ConfirmStudyApprovalFormData {
  originalApprover?: string;
}

const ConfirmStudyApproval: FC<ConfirmStudyApprovalProps> = ({ approvalID, status, callback, approvers }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const currentUserId = useSelector((state: State) => state.user.currentUser.id);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmStudyApprovalFormData>();

  const closeModal = () => dispatch({ type: 'CLOSE_MODAL' });

  const continueApproval = async (data: ConfirmStudyApprovalFormData) => {
    setLoading(true);
    try {
      await Http.post(apiRoute('approvals.requests.update', approvalID), {
        approver_id: _isNotBlank(data.originalApprover) ? Number(data.originalApprover) : currentUserId,
        response: status,
      });
      callback();
      successToast('Study approval updated successfully');
    } catch (error) {
      callback(error as Error);
      ExceptionHandler.captureException(
        new InVivoError('Failed to update study approval', {
          cause: error,
          slug: 'confirm-study-approval',
        })
      );
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const actionText = reviewActionToTextMap[status];

  return (
    <div className="ui__narrow-modal" data-test-component="ConfirmStudyApproval">
      <div className="pb3 tc">
        <h3 className="normal f4 pb2" data-testid="approval-title">
          {capitalise(actionText)} Review
        </h3>
        <p className="f6 pb2" data-testid="approval-description">
          Are you sure you would like to {actionText} this review? This action cannot be undone.
        </p>
      </div>
      <form onSubmit={handleSubmit(continueApproval)} data-testid="approval-form">
        {Auth.isCurrentTeamOwner() && (
          <div className={cn('pb2', { hasError: _notNil(errors.originalApprover) })}>
            <label htmlFor="originalApprover">{capitalise(actionText)} on behalf of</label>
            <select
              id="originalApprover"
              className="mb0"
              data-testid="approver-select"
              {...register('originalApprover', { required: 'Required' })}
            >
              {approvers.reduce<Array<JSX.Element>>((acc, approver) => {
                if (_isNil(approver.response)) {
                  const resolved = missingNameUseEmail(approver);
                  acc.push(
                    <option key={resolved.id} value={resolved.id}>
                      {resolved.name}
                    </option>
                  );
                }
                return acc;
              }, [])}
            </select>
          </div>
        )}
        <div>
          <button
            type="submit"
            disabled={loading}
            data-test-element="submit-button"
            data-testid="submit-button"
            className="button w-100 mb2"
          >
            {capitalise(actionText)} review
          </button>
          <button
            type="button"
            className="button plain w-100 db"
            disabled={loading}
            onClick={closeModal}
            data-testid="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfirmStudyApproval;
